/**
 * Demo page showcasing panorama viewer for IBS 2023
 * @module
 *
 */
import { createFileRoute } from "@tanstack/react-router";
import type { PanoramaViewerScene } from "gfx/panoramaViewerTypes";
import Toggle from "lib/components/actions/Toggle";
import styles from "lib/components/debug/panoramaViewer.module.scss";
import GPURequiredContainer from "lib/components/gfx/GPURequiredContainer";
import usePanoramaViewer from "lib/components/walkthrough/usePanoramaViewer";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";

const startingTextureUrl = "/static/debug/POC_Epcon_Bathroom_360.png";
const startingScene = { textureUrl: startingTextureUrl };

function DemoPhotorealInteriorPanoramaViewer() {
  const panoramaViewerContainerRef = useRef<HTMLDivElement>(null);

  const [scene, setScene] = useState<PanoramaViewerScene | undefined>(startingScene);

  const { animateCamera, setAnimateCamera, animationSpeed, setAnimationSpeed, resetCamera } = usePanoramaViewer({
    panoramaViewerContainerRef,
    scene,
    setScene,
  });

  useEffect(() => {
    document.body.style.overflow = "hidden"; // Important to prevent scrolling on iPad
    updateSlider();
  }, []);

  useEffect(() => {
    updateSlider();
  }, [animateCamera, animationSpeed]);

  function updateSlider() {
    const rangeInput: HTMLInputElement | null = document.querySelector("input[type=range]");
    if (!rangeInput) {
      return;
    }

    const rangeValue = rangeInput.valueAsNumber;
    const rangeMin = parseFloat(rangeInput.min);
    const rangeMax = parseFloat(rangeInput.max);
    const rangePercentage = (rangeValue - rangeMin) / (rangeMax - rangeMin);
    const percentageFromCenter = rangePercentage - 0.5;

    const rangeWidth = rangeInput.getBoundingClientRect().width;

    const rangeGauge: HTMLDivElement | null = document.querySelector(".rangeGauge");
    if (!rangeGauge) {
      return;
    }

    if (percentageFromCenter < 0) {
      rangeGauge.style.marginLeft = `${rangeWidth * rangePercentage}px`;
      rangeGauge.style.marginRight = "";
    } else {
      rangeGauge.style.marginLeft = `${rangeWidth * 0.5}px`;
      rangeGauge.style.marginRight = `${rangeWidth * rangePercentage}px`;
    }
    rangeGauge.style.width = `${rangeWidth * Math.abs(percentageFromCenter)}px`;
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Demo - Photoreal Interior")}</title>
      </Head>
      <div className={styles.panoramaViewerPage}>
        <div id="menuBar" className={styles.panoramaViewerMenuBar}>
          <button onClick={resetCamera}>Reset camera</button>

          <div className={styles.animateToggle}>
            <Toggle isTrue={animateCamera} onChange={() => setAnimateCamera(!animateCamera)} />
            <label>Animation</label>
          </div>
          {animateCamera && (
            <>
              <div className={styles.sliderWrapper}>
                <input
                  type="range"
                  min="-1.0"
                  max="1.0"
                  step="0.1"
                  value={animationSpeed}
                  onChange={(e) => setAnimationSpeed(e.currentTarget.valueAsNumber)}
                  onDoubleClick={() => setAnimationSpeed(0)}
                />
                <div className="rangeGaugeExtents" />
                <div className="rangeGauge" />
              </div>
              <label>Speed</label>
            </>
          )}
        </div>
        <GPURequiredContainer>
          <div className={styles.panoramaViewer} ref={panoramaViewerContainerRef} />
        </GPURequiredContainer>
      </div>
    </>
  );
}

export const Route = createFileRoute("/demo/photoreal-interior360-TS21DMPO80M1")({
  component: DemoPhotorealInteriorPanoramaViewer,
  staticData: { isPublicPage: true },
});
